import type { DrawState, ToolDefinition, BlendMode, CanvasLike, ToolConfig } from './types';
import { Tool } from './tool';
import { LayerClass } from './layer';

export { Tool, LayerClass };

// ─── Global State Object ───────────────────────────────────

class GlobalState implements DrawState {
  // Drawing flags
  drawing = false;
  erasing = false;
  zooming = false;
  movingSelection = false;
  isTransforming = false;
  isSelectionActive = false;

  // Active tool
  current_tool: ToolDefinition = Tool.Pen;

  // Pen properties
  pen_color = '#000000';
  pen_secondary_color = '#ffffff';
  pen_width = 2;
  pen_opacity = 1;
  pen_blur = 1;
  pen_type = 'solid';
  pen_cap: CanvasLineCap = 'round';
  pen_join: CanvasLineJoin = 'round';
  pen_tip: 'round' | 'square' | 'ellipse' | 'soft' | 'diamond' | 'vertical' | 'horizontal' | 'star' = 'round';
  pen_angle = 0;
  pen_hue = 0;
  cyclic_color = false;
  cyclic_color_speed = 5;

  // Brush properties
  brush_color = 'blue';
  brush_blur = 2;
  brush_hardness = 0.8;
  brush_flow = 1.0;
  brush_density = 100;
  brush_shape = 'circle';
  brush_style = 'default';
  brush_spacing = 1; // Default 1 spacing

  // Spray properties
  spray_radius = 100;
  spray_density = 100;

  // Canvas coordinates
  startX = 0;
  startY = 0;
  pX = 0;
  pY = 0;
  lastStampX = 0;
  lastStampY = 0;

  // Zoom
  zoom = 1;
  zoomFactor = 1.1;

  // Canvas dimensions
  image_width = 500;
  image_height = 500;
  image_bg_color = 'white';

  // Misc
  tool_icon_size = '24px';
  counter = 0;
  undo_index = -1;
  filepath = '';
  activeLayerIndex = 0;

  // Selection state
  selectionMask: ImageData | null = null;
  selectionBorder: import('./types').Point[][] = [];
  selectionPreviewBorder: import('./types').Point[][] = [];
  selectionCanvas: CanvasLike | null = null;
  inverseSelectionCanvas: CanvasLike | null = null;
  floatingContent: import('./types').FloatingContent | null = null;

  // Filter parameters
  shear_amount = 40;
  shear_horizontal = true;
  shear_direction = 1;
  melt_amount = 30;
  blur_radius = 3;
  gaussian_blur_radius = 2;
  mosaic_block_size = 10;

  // Fill tool
  fill_tolerance = 32;
  fill_all_layers = false;

  // Shape properties
  round_rect_corner_radius = 10;
  shape_fill = false;

  // Wand tool
  wand_tolerance = 32;
  wand_all_layers = false;

  // History limit
  max_undo_steps = 200;

  // Text tool
  text_size = 40;
  text_font = 'Roboto';

  // Undo state capture
  previousLayerState: ImageData | null = null;
  selectionDashOffset = 0;
  selectionDashOffsetMax = 8;

  // Documents (tabs)
  documents: import('./types').DocumentState[] = [];
  activeDocumentIndex = -1;

  // Tool config metadata  
  toolConfig: Record<string, ToolConfig> = {
    'btn-pen': { 
      props: [
        { label: 'Size', prop: 'pen_width', min: 1, max: 100, unit: 'px', mapping: 'int' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Cyclic Color', prop: 'cyclic_color', type: 'checkbox' },
        { label: 'Cycle Speed', prop: 'cyclic_color_speed', min: 1, max: 20, unit: '', mapping: 'int' },
      ] 
    },
    'btn-brush': {
      props: [
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Flow', prop: 'brush_flow', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Cyclic Color', prop: 'cyclic_color', type: 'checkbox' },
        { label: 'Cycle Speed', prop: 'cyclic_color_speed', min: 1, max: 20, unit: '', mapping: 'int' },
      ],
    },
    'btn-eraser': {
      props: [
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
      ],
    },
    'btn-spray': {
      props: [
        { label: 'Radius', prop: 'spray_radius', min: 1, max: 600, unit: 'px', mapping: 'int' },
        { label: 'Density', prop: 'spray_density', min: 1, max: 300, unit: '%', mapping: 'int' },
        { label: 'Cyclic Color', prop: 'cyclic_color', type: 'checkbox' },
        { label: 'Cycle Speed', prop: 'cyclic_color_speed', min: 1, max: 20, unit: '', mapping: 'int' },
      ],
    },
    'btn-line': {
      props: [
        { label: 'Size', prop: 'pen_width', min: 1, max: 100, unit: 'px', mapping: 'int' },
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
      ],
    },
    'btn-rect': {
      props: [
        { label: 'Size', prop: 'pen_width', min: 1, max: 100, unit: 'px', mapping: 'int' },
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Fill Shape', prop: 'shape_fill', type: 'checkbox' },
      ],
    },
    'btn-circle': {
      props: [
        { label: 'Size', prop: 'pen_width', min: 1, max: 100, unit: 'px', mapping: 'int' },
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Fill Shape', prop: 'shape_fill', type: 'checkbox' },
      ],
    },
    'btn-rounded-rect': {
      props: [
        { label: 'Size', prop: 'pen_width', min: 1, max: 100, unit: 'px', mapping: 'int' },
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Corner Radius', prop: 'round_rect_corner_radius', min: 0, max: 200, unit: 'px', mapping: 'int' },
        { label: 'Fill Shape', prop: 'shape_fill', type: 'checkbox' },
      ],
    },
    'btn-ellipse': {
      props: [
        { label: 'Size', prop: 'pen_width', min: 1, max: 100, unit: 'px', mapping: 'int' },
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Fill Shape', prop: 'shape_fill', type: 'checkbox' },
      ],
    },
    'btn-text': {
      props: [
        { label: 'Size', prop: 'text_size', min: 10, max: 200, unit: 'px', mapping: 'int' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Font', prop: 'text_font', type: 'select', items: ['Roboto', 'Arial', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana'] },
      ],
    },
    'btn-vector-select': {
      props: [
        { label: 'Size', prop: 'pen_width', min: 1, max: 100, unit: 'px', mapping: 'int' },
        { label: 'Hardness', prop: 'brush_hardness', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Opacity', prop: 'pen_opacity', min: 0, max: 100, unit: '%', mapping: 'percent' },
        { label: 'Corner Radius', prop: 'round_rect_corner_radius', min: 0, max: 200, unit: 'px', mapping: 'int' },
        { label: 'Fill Shape', prop: 'shape_fill', type: 'checkbox' },
      ],
    },
    'btn-wand': {
      props: [
        { label: 'Tolerance', prop: 'wand_tolerance', min: 0, max: 255, unit: '', mapping: 'int' },
        { label: 'All Layers', prop: 'wand_all_layers', type: 'checkbox' },
      ],
    },
    'btn-flood-fill': {
      props: [
        { label: 'Tolerance', prop: 'fill_tolerance', min: 0, max: 255, unit: '', mapping: 'int' },
        { label: 'All Layers', prop: 'fill_all_layers', type: 'checkbox' },
      ],
    },
  };

  // ─── Methods ────────────────────────────────────────────

  createCanvas(width: number, height: number): CanvasLike {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  initDefaultLayer(): void {
    try {
      // 1. Ensure layers exist
      if (layers.length === 0) {
        layers.push(new LayerClass('Background', this.image_width, this.image_height, (w, h) => this.createCanvas(w, h)));
        console.log('Background layer created successfully');
      }
      
      // 2. Ensure a corresponding document entry exists in g.documents
      // This is critical for the UI to remove 'no-document-active' class
      if (this.documents.length === 0) {
        this.documents.push({
          id: 'def_doc_' + Date.now(),
          name: 'Image 1',
          width: this.image_width,
          height: this.image_height,
          layers: [...layers],
          activeLayerIndex: 0,
          zoom: 1
        } as any);
        this.activeDocumentIndex = 0;
        console.log('Default document state created for new session');
      }
    } catch (err) {
      console.error('CRITICAL ERROR initializing layers:', err);
    }
  }
}

// ─── Singletons ────────────────────────────────────────────
// Ensure g is a true singleton across multiple loads (e.g. if Vite duplicates modules)
const w = (typeof window !== 'undefined' ? window : {}) as any;
const globalStateInstance = w.g || new GlobalState();
if (typeof window !== 'undefined') w.g = globalStateInstance;

/** THE global application state. Import `g` from this module wherever state is needed. */
export const g = globalStateInstance;

/** The active layers array. Mutated directly; references are shared. */
// Also ensure layers is a singleton
const layersInstance = w.layers || [];
if (typeof window !== 'undefined') w.layers = layersInstance;
export const layers: LayerClass[] = layersInstance;

/** Global event bus for cross-module communication. */
export const appEvents = new EventTarget();

// Initialize default layer if empty
if (layers.length === 0) {
    g.initDefaultLayer();
    // Notify potential listeners that initial state is ready
    setTimeout(() => {
        appEvents.dispatchEvent(new CustomEvent('document:switch'));
    }, 10);
}

console.log('Global state initialized (Singleton verified)');
